"use client";

import { motion } from "framer-motion";
import { Pause, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export default function MusicPlayer() {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);

        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);

        return () => {
            audio.removeEventListener("play", handlePause);
            audio.removeEventListener("pause", handlePause);
        };
    }, []);

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current
                .play()
                .then(() => setIsPlaying(true))
                .catch((err) => console.error("Playback error:", err));
        }
    };

    const handleRestart = () => {
        if (!audioRef.current) return;
        audioRef.current.currentTime = 0;
        audioRef.current
            .play()
            .then(() => setIsPlaying(true))
            .catch((err) => console.error("Playback error:", err));
    };

    const toggleMute = () => {
        if (!audioRef.current) return;
        audioRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    return (
        <div className="my-auto flex flex-col items-center justify-center gap-3">
            <audio
                ref={audioRef}
                src="/music/music.mp3"
                loop
                preload="metadata"
            />

            {/* Animated Sound Wave Bars (Increased Size, Up & Down Symmetrical Bounce) */}
            <div className="flex h-10 items-center justify-center gap-1.5">
                {[0.4, 0.8, 0.5, 0.9, 0.6, 0.8, 0.4].map((_, i) => (
                    <motion.span
                        key={i}
                        className="w-1.5 rounded-full bg-primary"
                        animate={
                            isPlaying
                                ? {
                                      height: [
                                          "15%",
                                          "100%",
                                          "30%",
                                          "90%",
                                          "15%",
                                      ],
                                  }
                                : { height: "20%" }
                        }
                        transition={{
                            duration: 1.6,
                            repeat: Infinity,
                            repeatType: "mirror",
                            delay: i * 0.25,
                            ease: "easeInOut",
                        }}
                    />
                ))}
            </div>

            {/* Control Buttons (Centered in Middle, Icons Only, Borderless) */}
            <div className="flex items-center justify-center gap-3">
                <button
                    type="button"
                    onClick={handleRestart}
                    title="Restart"
                    aria-label="Restart music"
                    className="text-text-color/80 flex size-8 items-center justify-center rounded-full transition hover:bg-primary/10 hover:text-primary active:scale-95"
                >
                    <RotateCcw className="size-4" />
                </button>

                <button
                    type="button"
                    onClick={togglePlay}
                    title={isPlaying ? "Pause" : "Play"}
                    aria-label={isPlaying ? "Pause music" : "Play music"}
                    className="flex size-9 items-center justify-center rounded-full bg-primary text-white shadow-sm transition hover:bg-primary/80 active:scale-95"
                >
                    {isPlaying ? (
                        <Pause className="size-4 fill-white" />
                    ) : (
                        <Play className="size-4 translate-x-0.5 fill-white" />
                    )}
                </button>

                <button
                    type="button"
                    onClick={toggleMute}
                    title={isMuted ? "Unmute" : "Mute"}
                    aria-label={isMuted ? "Unmute music" : "Mute music"}
                    className="text-text-color/80 flex size-8 items-center justify-center rounded-full transition hover:bg-primary/10 hover:text-primary active:scale-95"
                >
                    {isMuted ? (
                        <VolumeX className="size-4 text-danger" />
                    ) : (
                        <Volume2 className="size-4" />
                    )}
                </button>
            </div>
        </div>
    );
}
